import React, { useCallback, useEffect, useState } from 'react';
import { isNativePlatform } from '../../utils/platform';
import { captureEvent } from '../../posthog';
import BrandName from '../BrandName';

// KAN-21417 — first-launch disclaimer gate for Apple Review Guideline
// 5.3.4. Mirrors the iOS Expo gate at
// `apps/ios/components/onboarding/DisclaimerGate.tsx` so the Capacitor
// shell (iOS/Android WebView) and mobile-web visitors see the same
// "Analytics, Not a Sportsbook" surface on first launch.
//
// Storage key is shared verbatim across surfaces. Bump `_v1` → `_v2`
// only if Apple Review forces a copy revision so existing users
// re-acknowledge against the new text.
//
// Desktop browsers no-op. Their compliance surface is the Terms link
// in the global footer + the click-wrap attestation at signup; the
// gate is targeted at the mobile and native experiences where the
// footer chrome is less visible.

export const DISCLAIMER_STORAGE_KEY = '@aightbet/disclaimer_acknowledged_v1';

// Mobile breakpoint matches Tailwind's `md` (768px). Below this on the
// web SPA the user is treated as a mobile-web visitor and gets the
// gate. The desktop SPA is unaffected.
const MOBILE_MAX_WIDTH = 767;

type Status = 'loading' | 'pending' | 'acknowledged';

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

function isCapacitorShell(): boolean {
  try {
    return isNativePlatform();
  } catch {
    return false;
  }
}

function shouldGateThisSurface(): boolean {
  // Capacitor shells (iOS WebView, Android WebView) always gate.
  if (isCapacitorShell()) return true;
  // Web — only the mobile viewport gets the modal gate. Desktop
  // relies on the footer Terms link + signup click-wrap.
  return isMobileViewport();
}

function readAcknowledged(): boolean {
  try {
    return window.localStorage.getItem(DISCLAIMER_STORAGE_KEY) === '1';
  } catch {
    // localStorage can throw in Safari private mode / disabled
    // storage. Treat as unacknowledged so we never silently bypass.
    return false;
  }
}

function writeAcknowledged(): void {
  try {
    window.localStorage.setItem(DISCLAIMER_STORAGE_KEY, '1');
  } catch {
    /* no-op — see readAcknowledged */
  }
}

interface DisclaimerGateProps {
  children: React.ReactNode;
}

const DisclaimerGate: React.FC<DisclaimerGateProps> = ({ children }) => {
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    // Surface-check runs once on mount. We deliberately do NOT recompute
    // on viewport changes — a mid-session rotation should not yank the
    // user into a disclaimer flow.
    if (!shouldGateThisSurface()) {
      setStatus('acknowledged');
      return;
    }
    setStatus(readAcknowledged() ? 'acknowledged' : 'pending');
  }, []);

  useEffect(() => {
    if (status !== 'pending') return;
    captureEvent('disclaimer_shown', {
      storage_key_version: 1,
      surface: isCapacitorShell() ? 'capacitor' : 'mobile_web',
    });
  }, [status]);

  const onAcknowledge = useCallback(() => {
    writeAcknowledged();
    captureEvent('disclaimer_acknowledged', {
      storage_key_version: 1,
      surface: isCapacitorShell() ? 'capacitor' : 'mobile_web',
    });
    setStatus('acknowledged');
    // KAN-21625 — pin the post-acknowledge landing to `/` when the
    // visitor was at root. KAN-22544 (2026-05-20) narrows the pin to
    // root visitors only — deep-link destinations (e.g.
    // `/m/payment-success` after Stripe checkout) must be preserved so
    // the user lands where the redirect pointed, not on the marketing
    // landing.
    try {
      if (typeof window !== 'undefined' && window.history) {
        const path = window.location.pathname.replace(/\/+$/, '');
        if (path === '' || path === '/m') {
          window.history.replaceState(null, '', '/');
        }
      }
    } catch {
      /* no-op — history API not available */
    }
  }, []);

  if (status === 'loading') {
    // Hold render until the surface check + storage read complete. On
    // desktop this resolves synchronously on mount, so the children
    // appear without flash. On mobile, the gate-vs-children decision
    // is also synchronous — `localStorage` reads don't await — so the
    // null is effectively a single frame.
    return null;
  }

  if (status === 'pending') {
    return <DisclaimerSurface onAcknowledge={onAcknowledge} />;
  }

  return <>{children}</>;
};

const DisclaimerSurface: React.FC<{ onAcknowledge: () => void }> = ({
  onAcknowledge,
}) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-heading"
      data-testid="disclaimer-gate"
      data-surface={isCapacitorShell() ? 'capacitor' : 'mobile_web'}
      className="fixed inset-0 z-[9999] flex flex-col bg-[#030712] text-zinc-100"
    >
      <div
        className="flex-1 overflow-y-auto px-6 pb-6"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 1.25rem)' }}
      >
        <p className="mt-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-orange-400">
          Before you get started
        </p>
        <h1
          id="disclaimer-heading"
          className="mt-2 text-[28px] font-bold leading-tight text-zinc-50"
        >
          <BrandName variant="header" /> — Analytics, Not a Sportsbook
        </h1>
        <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-zinc-300">
          <p>
            <BrandName /> provides AI powered sports analytics and educational predictions. We are not a
            sportsbook and we do not accept, broker, or facilitate wagers of any kind.
          </p>
          <p>
            No money is wagered, deposited, or settled on this platform. Predictions, win rates, and ROI
            figures are performance metrics on our models, presented for informational and
            entertainment purposes.
          </p>
          <p>
            If you choose to use a separate, licensed sportsbook, consult your local laws first.
            <BrandName short /> has no relationship with any sportsbook and earns nothing from external bets
            you may or may not place.
          </p>
        </div>
      </div>
      <div
        className="px-6 pt-2"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <button
          type="button"
          onClick={onAcknowledge}
          className="w-full rounded-xl bg-orange-400 px-4 py-4 text-base font-semibold text-[#030712] transition-opacity active:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-200"
        >
          I Understand — Continue
        </button>
      </div>
    </div>
  );
};

export default DisclaimerGate;
